import React, { useState, useRef } from "react";
import cx from "classnames";
import { Map } from "immutable";
import { useDrag, useDrop } from "react-dnd";
import { Icon } from "@showitinc/showit-wc";
import AppConstants from "../../../constants/AppConstants";
import UIActions from "../../../actions/UIActions";
import {
  generateDragSpec,
  generateDropSpec,
  getBorderSide,
  getTargetArea,
} from "../../../utils/DnDUtils";
import { CANVAS_SET_BLOCK_LAYER_ITEM } from "../../../constants/DnDConstants";
import { pagePanelDrop } from "../SitePanelDropHandlers";
import LayerPanelCanvasSetMenu from "./LayerPanelCanvasSetMenu";
import openRowContextMenu from "../../../utils/openRowContextMenu";
import styles from "./LayerPanelCanvasSetBlock.module.css";

const {
  CANVAS_SET_LAYER_PANEL_CANVAS_SET,
  DROP_BORDER,
  ITEM_TYPE_BLOCK,
  ITEM_TYPE_CANVAS_SET_BLOCK,
  VISIBLE_DESKTOP,
  VISIBLE_MOBILE,
  VISIBLE_NONE,
} = AppConstants;

const VisibilityIcon = ({ visible }) =>
  ({
    [VISIBLE_MOBILE]: <Icon type="desktopOff" size="20" className={styles.visible} />,
    [VISIBLE_DESKTOP]: <Icon type="mobileOff" size="20" className={styles.visible} />,
    [VISIBLE_NONE]: (
      <div className={styles["device-container"]}>
        <Icon type="mobileOff" size="20" />
        <Icon type="desktopOff" size="20" />
      </div>
    ),
  }[visible] || null);

const LayerPanelCanvasSetBlock = ({
  block = Map(),
  canvasSet,
  id,
  index,
  layerType = ITEM_TYPE_CANVAS_SET_BLOCK,
  onLayerMove,
  onSelect,
  draggable = true,
  panel = CANVAS_SET_LAYER_PANEL_CANVAS_SET,
}) => {
  const [dropArea, setDropArea] = useState(null);
  const selfRef = useRef();

  const drag = {
    ...generateDragSpec({
      type: CANVAS_SET_BLOCK_LAYER_ITEM,
      item: {
        id,
        index,
        layerType,
      },
      onDrop: ({ data }) => onLayerMove(data),
    }),
    ...(!draggable ? { canDrag: () => false } : {}),
  };

  const drop = {
    ...generateDropSpec({
      type: CANVAS_SET_BLOCK_LAYER_ITEM,
      onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current)),
      onDrop: pagePanelDrop({ canvasSet, dropRef: selfRef.current, id, index, layerType }),
    }),
    ...(!draggable ? { canDrop: () => false } : {}),
  };

  const [{ isDragging }, dragRef] = useDrag(drag);
  const [{ canDrop, isOverCurrent }, dropRef] = useDrop(drop);

  const _rename = (e) => {
    e.stopPropagation();
    e.preventDefault();
    UIActions.toggleRenamePopup({
      title: "Canvas",
      itemType: ITEM_TYPE_BLOCK,
      itemId: block.get("id"),
      name: block.get("name"),
      slug: block.get("slug"),
      showWarning: true,
    });
  };

  const active = !!block.get("selected");
  const label = block.get("name");
  const visible = block.get("visible");

  const blockHeadingStyle = {};

  const themeClass = cx(styles.root, {
    [styles.active]: active,
    "layer-site-canvas": true,
    "layer-canvas-set": true,
  });

  if (canDrop && isOverCurrent && !isDragging) {
    const borderSide = getBorderSide(dropArea);
    blockHeadingStyle[borderSide] = DROP_BORDER;
  }

  return (
    <div
      id={id}
      className={`layer-block-wrapper ${active ? "active" : ""}`}
      ref={(self) => {
        selfRef.current = self;
        dropRef(self);
      }}
      onContextMenu={(e) =>
        openRowContextMenu({
          event: e,
          rowEl: selfRef.current,
          active,
          select: () => onSelect(block),
        })
      }
    >
      <div
        className={`layer-block ${themeClass}`}
        style={blockHeadingStyle}
        onClick={() => {
          onSelect(block);
        }}
        onDoubleClick={_rename}
        ref={dragRef}
      >
        <div className="layer-label listed">
          <div className={styles["left-container"]}>
            <Icon type="siteCanvas" size="20" />
            <div onDoubleClick={_rename}>{label}</div>
          </div>
          <div className={styles["right-container"]}>
            <VisibilityIcon className={styles["device-icon"]} visible={visible} />
            <LayerPanelCanvasSetMenu
              canvasSetId={canvasSet}
              id={id}
              panel={panel}
              active={active}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayerPanelCanvasSetBlock;
